/**
 * 1.7 – AI Phân loại & Sắp xếp Chứng cứ
 * 3 vùng: Panel trái (Tree phân loại 260px) | Timeline + danh sách chứng cứ | Panel phải AI Phân tích (280px).
 */
import { useState } from 'react'
import { useAppSelector } from '../../hooks/redux'
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
} from '@mui/material'
import { ExpandMore, Description, Warning, CompareArrows, CheckCircle, Star, StarBorder } from '@mui/icons-material'

const SOURCE_COLORS = { court: '#2563eb', plaintiff: '#ea580c', defendant: '#7c3aed' }

const TREE_DATA = [
  { id: 'court', label: 'Chứng cứ do Tòa án thu thập', count: 2, children: [
    { id: 'TA-001', label: 'Biên bản xác minh thực địa (TA-001)', date: '01/2023' },
    { id: 'TA-002', label: 'Kết quả giám định đất (TA-002)', date: '02/2023' },
  ]},
  { id: 'plaintiff', label: 'Chứng cứ do Nguyên đơn cung cấp', count: 2, children: [
    { id: 'ND-001', label: 'Giấy chứng nhận QSDD (ND-001)', date: '08/2023' },
    { id: 'ND-002', label: 'Hợp đồng mua bán (ND-002)', date: '10/2023', conflict: true },
  ]},
  { id: 'defendant', label: 'Chứng cứ do Bị đơn/người liên quan', count: 2, children: [
    { id: 'BD-001', label: 'Văn bản khiếu nại (BD-001)', date: '09/2023' },
    { id: 'BD-002', label: 'Biên lai nộp thuế (BD-002)', date: '04/2025' },
  ]},
]

interface EvidenceCard {
  id: string
  source: 'court' | 'plaintiff' | 'defendant'
  date: string
  code: string
  title: string
  excerpt: string
  conflict: boolean
  conflictWith?: string
}

const EVIDENCE_CARDS: EvidenceCard[] = [
  { id: 'TA-001', source: 'court', date: '01/2023', code: 'TA-001', title: 'Biên bản xác minh thực địa', excerpt: 'Kết quả xác minh tại thửa đất 123, tờ bản đồ 45. Hiện trạng sử dụng đất.', conflict: false },
  { id: 'TA-002', source: 'court', date: '02/2023', code: 'TA-002', title: 'Kết quả giám định đất', excerpt: 'Giám định viên kết luận diện tích, ranh giới theo hồ sơ địa chính.', conflict: false },
  { id: 'ND-001', source: 'plaintiff', date: '08/2023', code: 'ND-001', title: 'Giấy chứng nhận QSDD', excerpt: 'GCN QSDD số 123/2023, cấp cho hộ ông Nguyễn Văn A.', conflict: false },
  { id: 'ND-002', source: 'plaintiff', date: '10/2023', code: 'ND-002', title: 'Hợp đồng mua bán', excerpt: 'HĐMB ngày 15/10/2020. Có thể mâu thuẫn với kết quả giám định TA-002 về thời điểm.', conflict: true, conflictWith: 'TA-002' },
  { id: 'BD-001', source: 'defendant', date: '09/2023', code: 'BD-001', title: 'Văn bản khiếu nại', excerpt: 'Đơn khiếu nại lên UBND. Chưa có bản sao công chứng.', conflict: false },
  { id: 'BD-002', source: 'defendant', date: '04/2025', code: 'BD-002', title: 'Biên lai nộp thuế', excerpt: 'Biên lai thuế sử dụng đất các năm 2020–2024.', conflict: false },
]

function AIEvidencePage() {
  const user = useAppSelector((s) => s.auth.user)
  const [evidenceApproved, setEvidenceApproved] = useState(false)
  const [filterSource, setFilterSource] = useState<string | null>(null)
  const [filterId, setFilterId] = useState<string | null>(null)
  const [importantIds, setImportantIds] = useState<Set<string>>(new Set())
  const [viewDocId, setViewDocId] = useState<string | null>(null)
  const [comparePair, setComparePair] = useState<{ a: string; b: string } | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [snack, setSnack] = useState('')
  const isChief = user?.role === 'chief_judge' || user?.role === 'admin'

  const filteredCards = EVIDENCE_CARDS.filter((c) => {
    if (!filterSource && !filterId) return true
    if (filterId) return c.id === filterId
    return c.source === filterSource
  })

  const handleApplyFilter = () => {
    setSnack(filterSource || filterId ? 'Đã áp dụng bộ lọc.' : 'Đã phân tích toàn bộ chứng cứ.')
    setTimeout(() => setSnack(''), 2000)
  }

  const toggleImportant = (id: string) => {
    setImportantIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setSnack(importantIds.has(id) ? 'Đã bỏ đánh dấu quan trọng.' : 'Đã đánh dấu quan trọng.')
    setTimeout(() => setSnack(''), 2000)
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden' }}>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          UEL E-Court – Quản lý Chứng cứ (Hỗ trợ AI) – Vụ án: Tranh chấp QSDD tại Long An (2023/TLS-CDS)
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Panel trái – Phân loại & Bộ lọc (260px) */}
        <Paper elevation={0} sx={{ width: 260, borderRight: 1, borderColor: 'divider', p: 2, overflow: 'auto', flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Phân loại Chứng cứ & Bộ lọc</Typography>
          <Box sx={{ mt: 1 }}>
            {TREE_DATA.map((branch) => (
              <Accordion key={branch.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, border: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMore />} onClick={() => { setFilterId(null); setFilterSource(filterSource === branch.id ? null : branch.id); }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SOURCE_COLORS[branch.id as keyof typeof SOURCE_COLORS] }} />
                    <Typography variant="body2">{branch.label}</Typography>
                    <Chip label={branch.count} size="small" sx={{ height: 18, fontSize: 11 }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <List dense disablePadding>
                    {branch.children?.map((child) => (
                      <ListItem
                        key={child.id}
                        sx={{ py: 0.25, cursor: 'pointer', bgcolor: filterId === child.id ? 'action.selected' : 'transparent', borderRadius: 1 }}
                        onClick={() => { setFilterSource(null); setFilterId(filterId === child.id ? null : child.id); }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}><Description sx={{ fontSize: 16, color: 'text.secondary' }} /></ListItemIcon>
                        <ListItemText primary={child.label} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleApplyFilter}>Áp dụng Bộ lọc & Phân tích</Button>
        </Paper>

        {/* Vùng giữa – Timeline + Danh sách chứng cứ */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', p: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>Trục thời gian</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2, overflow: 'auto', pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption">Tháng 1/2023</Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.plaintiff, mt: 0.5 }} title="ND-001" />
            </Box>
            <Box sx={{ flex: 1, height: 2, bgcolor: 'divider', minWidth: 40 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption">Tháng 6/2023</Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.court, mt: 0.5 }} title="TA-001" />
            </Box>
            <Box sx={{ flex: 1, height: 2, bgcolor: 'divider', minWidth: 40 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption">Tháng 4/2025</Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.defendant, mt: 0.5 }} title="BD-002" />
            </Box>
          </Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Danh sách chứng cứ {filterSource || filterId ? `(${filteredCards.length})` : ''}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto', flex: 1 }}>
            {filteredCards.map((card) => (
              <Card key={card.id} variant="outlined" sx={{ flexShrink: 0, borderColor: importantIds.has(card.id) ? 'primary.main' : undefined }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS[card.source] }} />
                    <Typography variant="body2" fontWeight={600}>{card.date} – {card.code} {card.title}</Typography>
                    {importantIds.has(card.id) && <Star color="primary" fontSize="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">{card.excerpt}</Typography>
                  {card.conflict && (
                    <Chip icon={<Warning />} label={`⚠️ AI phát hiện mâu thuẫn với ${card.conflictWith}`} size="small" color="error" sx={{ mt: 1 }} />
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setViewDocId(card.id)}>Xem</Button>
                    <Button size="small" onClick={() => { setSnack('Đã lưu chú thích (demo).'); setTimeout(() => setSnack(''), 2000); }}>Chú thích</Button>
                    <Button size="small" startIcon={importantIds.has(card.id) ? <Star /> : <StarBorder />} onClick={() => toggleImportant(card.id)}>
                      {importantIds.has(card.id) ? 'Bỏ đánh dấu' : 'Đánh dấu quan trọng'}
                    </Button>
                    {card.conflict && card.conflictWith && (
                      <Button size="small" startIcon={<CompareArrows />} onClick={() => setComparePair({ a: card.id, b: card.conflictWith! })}>So sánh ngay</Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Panel phải – AI Phân tích (280px) */}
        <Paper elevation={0} sx={{ width: 280, borderLeft: 1, borderColor: 'divider', p: 2, overflow: 'auto', flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>🤖 AI Phân tích & Sắp xếp</Typography>
          <Typography variant="body2" color="text.secondary">Tổng số chứng cứ: 12</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Bổ sung chứng cứ: 3 (ND-001, ND-002, BD-001)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Có thể mâu thuẫn: 2 (ND-002 ↔ TA-002)</Typography>
          <Typography variant="caption" fontWeight={600} sx={{ mt: 2, display: 'block' }}>📌 Gợi ý ưu tiên xem xét:</Typography>
          <Box component="ol" sx={{ pl: 2, mt: 0.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">ND-002 – Mâu thuẫn ngày ký với biên bản TA-002</Typography></li>
            <li><Typography variant="body2">BD-001 – Thiếu công chứng</Typography></li>
          </Box>
          <Button size="small" variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => setReportOpen(true)}>Xem báo cáo chi tiết</Button>
          <Button size="small" variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => setSummaryOpen(true)}>Tạo bản tóm tắt</Button>
          {isChief && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>Chánh án phê duyệt</Typography>
              {evidenceApproved ? (
                <Alert severity="success" icon={<CheckCircle />}>Đã phê duyệt phân tích chứng cứ đưa vào hồ sơ chính thức.</Alert>
              ) : (
                <Button variant="contained" color="primary" fullWidth startIcon={<CheckCircle />} onClick={() => setEvidenceApproved(true)}>
                  Phê duyệt phân tích chứng cứ đưa vào hồ sơ
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={!!viewDocId} onClose={() => setViewDocId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Xem chứng cứ – {viewDocId}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {EVIDENCE_CARDS.find((c) => c.id === viewDocId)?.excerpt ?? ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">Nội dung đầy đủ (file PDF/tài liệu) sẽ hiển thị tại đây khi tích hợp thật.</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={!!comparePair} onClose={() => setComparePair(null)} maxWidth="sm" fullWidth>
        <DialogTitle>So sánh chứng cứ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" fontWeight={600}>{comparePair?.a} vs {comparePair?.b}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            AI phát hiện mâu thuẫn về thời điểm/ngày ký giữa hai tài liệu. Nên xem xét làm rõ với các bên.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Báo cáo chi tiết chứng cứ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>Tổng số: 6 chứng cứ. Nguồn: Tòa án 2, Nguyên đơn 2, Bị đơn 2.</Typography>
          <Typography variant="body2" paragraph>Mâu thuẫn: ND-002 ↔ TA-002. Gợi ý ưu tiên xem: ND-002 (ngày ký), BD-001 (thiếu công chứng).</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bản tóm tắt chứng cứ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>Đã tạo bản tóm tắt (demo). Trong phiên bản thật sẽ xuất file Word/PDF.</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snack} message={snack} autoHideDuration={2000} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  )
}

export default AIEvidencePage
